import { useContext, useRef, useEffect, useState } from "react"
import { LocaleContext } from "contexts"
import cc from "classcat"
import Slider from "react-slick"
import H2 from "components/atoms/H2"
import useTranslations from "hooks/useTranslations"
import CustomSliderDot from "components/atoms/common/CustomSliderDot"
import { logosByLocale, customLogosByPage } from "./logos"

const sliderSettings = {
  slidesToShow: 8,
  autoplay: false,
  autoplaySpeed: 2000,
  speed: 1000,
  pauseOnHover: false,
  pauseOnDotsHover: true,
  pauseOnFocus: true,
  focusOnSelect: true,
  swipeToSlide: true,
  responsive: [
    {
      breakpoint: 1536,
      settings: {
        slidesToShow: 7,
      },
    },
    {
      breakpoint: 1280,
      settings: {
        slidesToShow: 5,
      },
    },
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 4,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 3,
      },
    },
    {
      breakpoint: 640,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
  ],
}

const LogoCloudAnimated = ({
  center = false,
  currentPage,
  title = "",
  rows = 1,
  showArrows = false,
  subtitle = "",
}) => {
  const { locale } = useContext(LocaleContext)
  const { ourCustomers } = useTranslations()
  const logos =
    customLogosByPage?.[currentPage]?.[locale] || logosByLocale[locale]

  const containerRef = useRef<HTMLDivElement>(null)
  const sliderRefs = useRef<(Slider | null)[]>([])
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting)
      },
      { threshold: 0.5 },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    sliderRefs.current.forEach((slider) => {
      if (slider) {
        if (isVisible) {
          slider.slickPlay()
        } else {
          slider.slickPause()
        }
      }
    })
  }, [isVisible])

  const showControlsSettings = showArrows
    ? {
        slidesToShow: 8,
        autoplaySpeed: 2000,
        speed: 800,
        // dots: true,
        arrows: true,
        slidesToScroll: 2,
        dotsClass: "slick-dots green-on-black",
        customPaging: () => (
          <a>
            <CustomSliderDot />
          </a>
        ),
      }
    : {}
  return (
    <div className="relative my-16 overflow-x-hidden bg-white lg:my-24">
      <div
        className={cc([
          "mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:mb-16 lg:px-8",
          center ? "text-center" : "",
        ])}
      >
        <H2 textAlign="center">{title || ourCustomers}</H2>
        {subtitle && (
          <p className="mx-auto max-w-3xl text-lg text-zinc-700">{subtitle}</p>
        )}
      </div>
      <div
        ref={containerRef}
        className={cc([
          "space-y-2 sm:px-0 md:gap-x-6 lg:w-full",
          center ? "mx-auto md:justify-center" : "",
        ])}
      >
        {Array.from({ length: rows }, (_, i) => {
          const odd = i % 2 === 1
          let renderedArray
          if (rows > 1) {
            const halfIndex = Math.ceil(logos.length / 2)
            const firstRow = logos.slice(0, halfIndex)
            const secondRow = logos.slice(halfIndex)
            if (odd) {
              renderedArray = secondRow
            } else {
              renderedArray = firstRow
            }
          } else {
            renderedArray = logos
          }
          return (
            <Slider
              ref={(el) => (sliderRefs.current[i] = el)}
              {...sliderSettings}
              {...showControlsSettings}
              rtl={odd}
              className={cc(["logoCloud", showArrows ? " arrows" : ""])}
              key={i}
            >
              {renderedArray.map((logo) => (
                <div className="h-full text-center">
                  <logo.image
                    key={logo.name}
                    loading="lazy"
                    className={cc([
                      "mx-auto flex max-h-24 first:pl-0 last:pr-0 md:max-h-40 md:px-0",
                      {
                        "md:w-36": !logo.className.includes("w-"),
                      },
                      logo.className,
                    ])}
                  />
                </div>
              ))}
            </Slider>
          )
        })}
      </div>
    </div>
  )
}

export default LogoCloudAnimated
